// Workflow configurations for scan-gated operations
// Based on WMS-Automated-Barcode-and-Order-Integration spec

import { BarcodeType } from '@/shared/lib/barcodeParser';

/**
 * Receiving Workflow
 * Flow: NF-e/CT-e Scan → Item Scans → Location Scan
 */
export const receivingWorkflowSteps = [
  {
    id: 'scan-document',
    type: 'document',
    label: 'Scan Invoice',
    instruction: 'Scan the NF-e, CT-e, or invoice barcode to retrieve ASN data and expected items.',
    scanMode: 'nfe',
    expectedTypes: [BarcodeType.NFE_ACCESS_KEY, BarcodeType.CTE_ACCESS_KEY, BarcodeType.NFE_QR],
    placeholder: 'Scan NF-e/CT-e barcode...',
    triggersAction: 'load_asn'
  },
  {
    id: 'scan-items',
    type: 'item',
    label: 'Scan Items',
    instruction: 'Scan each item to verify SKU, lot number, and expiry date against the ASN.',
    scanMode: 'gs1',
    expectedTypes: [BarcodeType.GS1_128, BarcodeType.GS1_DATAMATRIX, BarcodeType.EAN_13],
    placeholder: 'Scan item barcode (GS1-128)...',
    matchItems: true, // Match against expected items
    triggersAction: 'validate_item'
  },
  {
    id: 'scan-location',
    type: 'location',
    label: 'Assign Location',
    instruction: 'Scan the bin/rack barcode where items will be stored.',
    scanMode: 'internal',
    expectedTypes: [BarcodeType.INTERNAL],
    placeholder: 'Scan location barcode (LOC-X-XX-XX)...',
    triggersAction: 'assign_location'
  }
];

/**
 * Picking Workflow
 * Flow: Order Scan → Location Scan → Item Scan (repeat for each item)
 */
export const pickingWorkflowSteps = [
  {
    id: 'scan-order',
    type: 'document',
    label: 'Scan Order',
    instruction: 'Scan the pick slip or order barcode to load items and locations.',
    scanMode: 'internal',
    expectedTypes: [BarcodeType.INTERNAL],
    placeholder: 'Scan order/pick slip barcode (SO-XXXX-XXXX)...',
    triggersAction: 'load_pick_list'
  },
  {
    id: 'scan-location',
    type: 'location',
    label: 'Verify Location',
    instruction: 'Scan the bin location barcode to confirm you are at the correct pick location.',
    scanMode: 'internal',
    expectedTypes: [BarcodeType.INTERNAL],
    placeholder: 'Scan location barcode (LOC-X-XX-XX)...',
    triggersAction: 'verify_location'
  },
  {
    id: 'scan-item',
    type: 'item',
    label: 'Scan Item',
    instruction: 'Scan the item barcode to confirm correct product picked.',
    scanMode: 'gs1',
    expectedTypes: [BarcodeType.GS1_128, BarcodeType.EAN_13, BarcodeType.GS1_DATAMATRIX],
    placeholder: 'Scan item barcode...',
    matchItems: true,
    triggersAction: 'confirm_pick'
  }
];

/**
 * Packing Workflow
 * Flow: Order/Tote Scan → Item Verification Scans → Carton Scan
 */
export const packingWorkflowSteps = [
  {
    id: 'scan-tote',
    type: 'document',
    label: 'Scan Tote/Order',
    instruction: 'Scan the order tote or pick slip to load items for packing verification.',
    scanMode: 'internal',
    expectedTypes: [BarcodeType.INTERNAL],
    placeholder: 'Scan tote or order barcode...',
    triggersAction: 'load_pack_list'
  },
  {
    id: 'verify-items',
    type: 'item',
    label: 'Verify Contents',
    instruction: 'Re-scan each item as you pack it into the carton to verify order accuracy.',
    scanMode: 'gs1',
    expectedTypes: [BarcodeType.GS1_128, BarcodeType.EAN_13],
    placeholder: 'Scan item into carton...',
    matchItems: true,
    triggersAction: 'verify_pack_item'
  },
  {
    id: 'scan-carton',
    type: 'item',
    label: 'Scan Carton',
    instruction: 'Scan or assign a carton barcode to seal the package.',
    scanMode: 'internal',
    expectedTypes: [BarcodeType.INTERNAL],
    placeholder: 'Scan carton barcode (CTN-XXXX)...',
    triggersAction: 'seal_carton'
  }
];

/**
 * Dispatch Workflow
 * Flow: Vehicle Scan → Pallet/Carton Scans → Confirm Load
 */
export const dispatchWorkflowSteps = [
  {
    id: 'scan-vehicle',
    type: 'vehicle',
    label: 'Scan Vehicle',
    instruction: 'Scan the vehicle or trailer barcode to start loading.',
    scanMode: 'internal',
    expectedTypes: [BarcodeType.INTERNAL],
    placeholder: 'Scan vehicle ID...',
    triggersAction: 'start_load'
  },
  {
    id: 'scan-pallets',
    type: 'item',
    label: 'Load Pallets/Cartons',
    instruction: 'Scan each pallet or carton as you load it onto the vehicle.',
    scanMode: 'internal',
    expectedTypes: [BarcodeType.INTERNAL],
    placeholder: 'Scan pallet (PLT-XXX) or carton (CTN-XXX)...',
    matchItems: true,
    triggersAction: 'load_item'
  },
  {
    id: 'confirm-dispatch',
    type: 'document',
    label: 'Confirm Dispatch',
    instruction: 'Scan the BOL or dispatch confirmation barcode to complete loading.',
    scanMode: 'internal',
    expectedTypes: [BarcodeType.INTERNAL],
    placeholder: 'Scan BOL barcode...',
    triggersAction: 'confirm_dispatch'
  }
];

// Sample expected items for demo purposes
export const sampleReceivingItems = [
  { sku: 'SKU-A1001', gtin: '07891234567890', quantity: 10, lot: 'LOT123456', expiry: '2025-12-31' },
  { sku: 'SKU-B2002', gtin: '07891234567891', quantity: 5, lot: 'LOT123457', expiry: '2025-06-30' },
  { sku: 'SKU-C3003', gtin: '07891234567892', quantity: 20, lot: 'LOT123458', expiry: '2024-12-31' },
];

export const samplePickingItems = [
  { sku: 'SKU-A1001', gtin: '07891234567890', quantity: 2, location: 'LOC-A-01-05' },
  { sku: 'SKU-B2002', gtin: '07891234567891', quantity: 1, location: 'LOC-B-02-03' },
];

export const samplePackingItems = [
  { sku: 'SKU-A1001', gtin: '07891234567890', quantity: 2 },
  { sku: 'SKU-B2002', gtin: '07891234567891', quantity: 1 },
];

export const sampleDispatchItems = [
  { id: 'PLT-0001234567', type: 'pallet', orders: 3 },
  { id: 'CTN-0001234568', type: 'carton', orders: 1 },
  { id: 'CTN-0001234569', type: 'carton', orders: 1 },
];
